import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "helpers/cn";

const textareaVariants = cva(
  "flex w-full min-w-0 border border-input bg-card text-foreground transition-colors placeholder:text-subtle aria-invalid:border-destructive focus-visible:border-ring focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60",
  {
    variants: {
      size: {
        default: "min-h-8 resize-y rounded-lg px-2.75 py-1 text-body-small leading-5.5",
        large: "min-h-11 resize-y rounded-[10px] px-3.25 py-1.75 text-body",
        touch: "min-h-24 resize-none rounded-lg px-3.5 py-2.5 text-body"
      }
    },
    defaultVariants: { size: "default" }
  }
);

type Props = Omit<ComponentProps<"textarea">, "size"> &
  VariantProps<typeof textareaVariants> & {
    showCount?: boolean;
  };

const Textarea = ({ size, showCount, className, ...props }: Props) => {
  const count = String(props.value ?? "").length;
  const outOfRange = showCount && props.maxLength != null && count > props.maxLength;

  const field = (
    <textarea
      aria-invalid={outOfRange || undefined}
      className={cn(textareaVariants({ size }), className)}
      data-slot="textarea"
      {...props}
    />
  );

  if (!showCount) return field;

  return (
    <div className="relative w-full" data-slot="textarea-group">
      {field}
      <span
        className={cn(
          "pointer-events-none absolute end-0 -bottom-5.5 text-body-small leading-5.5 whitespace-nowrap",
          outOfRange ? "text-destructive" : "text-subtle"
        )}
      >
        {props.maxLength == null ? count : `${count} / ${props.maxLength}`}
      </span>
    </div>
  );
};

export { Textarea, textareaVariants };
